import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/currency-input";
import { FloppyDisk, CircleNotch } from "@phosphor-icons/react";
import { throwIfNotOk, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import {
  SERVICE_PRICE_MIN_KEY,
  SERVICE_PRICE_MAX_KEY,
  parseLimit,
  formatLimit,
} from "@/lib/service-price-limits-shared";

export function ServicePriceLimitsSection() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await fetch("/api/settings");
      await throwIfNotOk(res, "Greška pri učitavanju postavki");
      return (await res.json()) as Record<string, string>;
    },
  });

  const [min, setMin] = useState<number | null>(null);
  const [max, setMax] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!settings || loaded) return;
    setMin(parseLimit(settings[SERVICE_PRICE_MIN_KEY]));
    setMax(parseLimit(settings[SERVICE_PRICE_MAX_KEY]));
    setLoaded(true);
  }, [settings, loaded]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiSend(
        "/api/settings",
        "PATCH",
        {
          [SERVICE_PRICE_MIN_KEY]: min === null ? "" : String(min),
          [SERVICE_PRICE_MAX_KEY]: max === null ? "" : String(max),
        },
        "Greška pri spremanju",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      queryClient.invalidateQueries({ queryKey: ["service-price-limits"] });
      toast.success("Granice cijena spremljene");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rangeInvalid = min !== null && max !== null && min > max;
  const savedMin = parseLimit(settings?.[SERVICE_PRICE_MIN_KEY]);
  const savedMax = parseLimit(settings?.[SERVICE_PRICE_MAX_KEY]);
  const isDirty = min !== savedMin || max !== savedMax;

  const summary =
    min === null && max === null
      ? "Trenutno nema ograničenja: terapeuti mogu unijeti bilo koju cijenu."
      : min !== null && max !== null
        ? `Terapeuti mogu unijeti cijenu bez PDV-a od ${formatLimit(min)} do ${formatLimit(max)} KM.`
        : min !== null
          ? `Terapeuti ne mogu unijeti cijenu bez PDV-a nižu od ${formatLimit(min)} KM.`
          : `Terapeuti ne mogu unijeti cijenu bez PDV-a višu od ${formatLimit(max as number)} KM.`;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-foreground">Granice cijene usluge</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Odnosi se na cijenu bez PDV-a koju terapeut upisuje (prije provizije i regionalnog
        multiplikatora); klijent plaća taj iznos uvećan za PDV. Ostavite polje prazno da ta granica ne važi. Postojeće usluge izvan raspona
        nastavljaju raditi dok im se cijena ne promijeni.
      </p>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Učitavanje...</div>
      ) : (
        <>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="service-price-min">Minimalna cijena</Label>
              <CurrencyInput
                id="service-price-min"
                value={min}
                onChange={setMin}
                placeholder="bez granice"
                aria-invalid={rangeInvalid}
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="service-price-max">Maksimalna cijena</Label>
              <CurrencyInput
                id="service-price-max"
                value={max}
                onChange={setMax}
                placeholder="bez granice"
                aria-invalid={rangeInvalid}
              />
            </div>
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={!isDirty || rangeInvalid || saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <CircleNotch className="size-4 animate-spin" />
              ) : (
                <FloppyDisk className="size-4" />
              )}
              Sačuvaj
            </Button>
          </div>

          <p className={rangeInvalid ? "mt-2 text-xs text-destructive" : "mt-2 text-xs text-muted-foreground"}>
            {rangeInvalid ? "Minimalna cijena ne može biti veća od maksimalne." : summary}
          </p>
        </>
      )}
    </div>
  );
}
